import type { Catalog } from './catalog';

export const EN_FALLBACK: Catalog = {
	served_view: {
		'loading.sharedView': 'Loading shared view…',
		'error.generic': 'Something went wrong',
		'error.unableToLoad': 'Unable to Load View',
		'error.notAvailable': 'View not available',
		'error.notPublished': 'This view has not been published yet.',
		'permission.viewOnly': 'View only',
		'permission.canSubmit': 'Can submit',
		'permission.fullAccess': 'Full access',
	},
	generated_app: {
		'feedback.likePrompt': 'What did you like about this view?',
	},
	// Trace-panel labels. The backend emits these keys alongside the English it
	// already sent, so the viewer can render a stored trace in the reader's
	// language — the same run shows Japanese to the customer and English to
	// whoever is supporting them. Phase names mirror PHASE_DEFINITIONS in
	// cn_controller/core/execution_hooks.py; keep the two in step.
	trace_viewer: {
		'phase.queued': 'Queued',
		'phase.dispatchWait': 'Dispatch Wait',
		'phase.workerSetup': 'Worker Setup',
		'phase.entityValidation': 'Entity Validation',
		'phase.dataSourceCalls': 'Data Source Calls',
		'phase.fileProcessing': 'File Processing',
		'phase.collation': 'Collation',
		'phase.validation': 'Validation',
		'phase.policyEvaluation': 'Policy Evaluation',
		'phase.agentRemediation': 'Agent Remediation',
		'phase.narrativeGeneration': 'Narrative Generation',
		'phase.resultWriteBack': 'Result Write-back',
		'phase.complianceWriteBack': 'Compliance Write-back',
		'operation.extractInstanceData': 'Extract Instance Data',
		'operation.loadPolicySets': 'Load Policy Sets',
		'operation.evaluatePolicies': 'Evaluate Policies',
		'operation.executeValidationRules': 'Execute Validation Rules',
		'operation.generateNarrativeSummary': 'Generate Narrative Summary',
		'operation.executeAgentPlan': 'Execute Agent Plan',
		'operation.processEntityFiles': 'Process Entity Files',
		'operation.resolveCollationRules': 'Resolve Collation Rules',
		'operation.graphEnrichmentInput': 'Graph Enrichment — Input',
		'operation.graphEnrichmentResults': 'Graph Enrichment — Results',
		'operation.graphqlFetch': 'GraphQL: {{name}}',
		'operation.apiFetch': 'API: {{name}}',
		'operation.extractFile': 'Extract {{filename}}',
		'operation.downloadFile': 'Download {{filename}}',
		'operation.classifyDocument': 'Classify Document',
		'operation.graphEnrichmentRule': 'Graph Enrichment: {{name}}',
		'operation.graphEnrichmentSummary': 'Graph Enrichment — Summary',
		'operation.graphRuleContext': 'Graph Rule Context',
		'operation.ruleInputs': 'Rule Inputs: {{name}}',
		'operation.collateData': 'Collate Data',
		'operation.reuseDataSources': 'Reuse Data Sources',
		'operation.policyInputs': 'Policy Evaluation Inputs',
		'operation.narrativeInputs': 'Narrative Inputs',
		// trace-viewer chrome — static UI strings, resolved via real t() calls
		// (not backend-authored, unlike the phase/operation keys above).
		'ui.header.status': 'STATUS',
		'ui.header.duration': 'DURATION',
		'ui.header.latency': 'LATENCY',
		'ui.header.qTime': 'Q-TIME',
		'ui.header.runId': 'RUN ID',
		'ui.viewMode.internal': 'Internal',
		'ui.viewMode.customer': 'Customer',
		'ui.runWaterfall': 'Run Waterfall',
		'ui.createAnnotation': 'Create annotation',
		'ui.annotationCreated': 'Annotation created',
		'ui.creatingAnnotation': 'Creating...',
		'ui.previousTrace': 'Previous trace',
		'ui.nextTrace': 'Next trace',
		'ui.close': 'Close',
		'ui.expandTraceTree': 'Expand trace tree',
		'ui.backToWaterfall': 'Back to waterfall',
		'ui.zoomOutFully': 'Zoom out fully',
		'ui.zoomOut': 'Zoom out',
		'ui.resetZoom': 'Reset zoom to 100%',
		'ui.zoomIn': 'Zoom in',
		'ui.zoomInFully': 'Zoom in fully',
		'ui.traceTree': 'Trace Tree',
		'ui.collapseTraceTree': 'Collapse trace tree',
		'ui.searchPlaceholder': 'Search trace...',
		'ui.collapseAll': 'Collapse all',
		'ui.expandAll': 'Expand all',
		'ui.clearTraceSearch': 'Clear trace search',
		'ui.filterTraceSteps': 'Filter trace steps',
		'ui.status': 'Status',
		'ui.spanKind': 'Span kind',
		'ui.traceDetails': 'Trace Details',
		'ui.tab.debug': 'Debug',
		'ui.tab.rawJson': 'RAW JSON',
		'ui.details.drawContext': 'Draw Context',
		'ui.details.entityData': 'Entity Data',
		'ui.details.budgetReconciliation': 'Budget Reconciliation',
		'ui.details.collationRules': 'Collation Rules',
		'ui.details.details': 'Details',
		'ui.label.text': 'TEXT',
		'ui.label.inputJson': 'INPUT JSON',
		'ui.label.outputJson': 'OUTPUT JSON',
		'ui.label.json': 'JSON',
		'ui.label.fullJson': 'FULL JSON',
		'ui.loadingStepDetails': 'Loading step details',
		'ui.result': 'Result',
		'ui.analysis': 'Analysis',
		// Graph enrichment (collation). "Fields Read" counts extraction fields,
		// not provisions — a catch-all field expands into several graph nodes.
		'ui.graphEnrichment.sourceDocument': 'Source Document',
		'ui.graphEnrichment.fieldsRead': 'Fields Read',
		'ui.graphEnrichment.provisionMap': 'Provision Map',
		'ui.annotation.placeholder': 'What should the next run do differently?',
		'ui.highlights': 'Highlights',
		'ui.metric.operation': 'Operation',
		'ui.metric.model': 'Model',
		'ui.metric.inputTokens': 'Input Tokens',
		'ui.metric.outputTokens': 'Output Tokens',
		'ui.metric.totalTokens': 'Total Tokens',
		'ui.metric.cost': 'Cost',
		'ui.metric.latency': 'Latency',
		'ui.metric.cache': 'Cache',
		'ui.metric.type': 'Type',
		'ui.metric.qTime': 'Q-Time',
		'ui.header.tokensCost': 'TOKENS / COST',
		'ui.metric.duration': 'Duration',
		'ui.metric.status': 'Status',
		'ui.metric.startedAt': 'Started At',
		'ui.metric.completedAt': 'Completed At',
		'ui.metric.inputSummary': 'Input Summary',
		'ui.metric.outputSummary': 'Output Summary',
		'ui.metric.startTime': 'Start Time',
		'ui.metric.endTime': 'End Time',
		'ui.metric.operations': 'Operations',
		'ui.metric.tokensCost': 'Tokens / Cost',
		'ui.noTraceData': 'No trace data available',
		'ui.requiresHumanValidation': 'Requires human validation',
		'ui.instanceData': 'Instance Data',
		'ui.noData': 'No data available',
		'ui.label.response': 'RESPONSE',
		'ui.collation.distinctFilesIncluded': 'Distinct files included: {{total}}',
		'ui.collation.fileInputRecords': 'File input records: {{total}}',
		'ui.collation.fileInputs': 'File inputs',
		'ui.details.additionalFileDetails': 'Additional file details',
		'ui.details.additionalRuleDetails': 'Additional rule details',
		'ui.details.affectedFile': 'Affected file: {{files}}',
		'ui.details.affectedFiles': 'Affected files: {{files}}',
		'ui.details.affectedPhases': 'Affected phases',
		'ui.details.agentActions.changesMade': 'Changes Made',
		'ui.details.agentActions.completed': 'Actions completed',
		'ui.details.agentActions.confidence': '{{percent}}% confidence',
		'ui.details.agentActions.failed': 'Failed',
		'ui.details.agentActions.finalAnswer': 'Final Answer',
		'ui.details.agentActions.humanReview': 'Requires human review',
		'ui.details.agentActions.notInvoked':
			'The remediation agent was not invoked.',
		'ui.details.agentActions.recommendations': 'Recommendations',
		'ui.details.agentActions.steps': 'Steps',
		'ui.details.agentActions.successful': 'Successful',
		'ui.details.agentActions.summary': 'Summary',
		'ui.details.agentActions.task': 'Task',
		'ui.details.agentActions.title': 'Agent Actions',
		'ui.details.agentActions.toolCalls': 'Tool calls',
		'ui.details.agentActions.traceLimited':
			'Some agent trace details were shortened or omitted to fit the trace message size limit.',
		'ui.details.agentActions.violationsAddressed': 'Violations addressed',
		'ui.details.budget.costCode': 'Cost code: {{code}}',
		'ui.details.budget.documentedAmount': 'Documented amount',
		'ui.details.budget.lineItem': 'Line item',
		'ui.details.budget.matchConfidence': 'Match confidence',
		'ui.details.budget.matchedDocument': 'Matched document',
		'ui.details.budget.overBudget': 'Over budget',
		'ui.details.budget.overage': 'Overage',
		'ui.details.budget.previouslyFunded': 'Previously funded',
		'ui.details.budget.withinBudget': 'Within budget',
		'ui.details.checklist': 'Checklist',
		'ui.details.checklistItem': 'Checklist item',
		'ui.details.checklistItemColumn': 'Item',
		'ui.details.collapseRule': 'Collapse {{rule}}',
		'ui.details.collation.completed': 'Completed',
		'ui.details.collation.errors': 'Errors',
		'ui.details.collation.skipped': 'Skipped',
		'ui.details.collation.totalRules': 'Rules',
		'ui.details.collationFieldsUsed': '{{total}} fields used',
		'ui.details.complianceNotes': 'Compliance Notes:',
		'ui.details.confidence': 'Confidence',
		'ui.details.copyRuleJson': 'Copy {{rule}} raw JSON',
		'ui.details.expandRule': 'Expand {{rule}}',
		'ui.details.failure.executionFailed': 'Execution Failed',
		'ui.details.failure.executionPartial': 'Execution Partially Completed',
		'ui.details.failure.workflowFailed': '{{workflow}} Failed',
		'ui.details.failure.workflowPartial': '{{workflow}} Partially Completed',
		'ui.details.fileProcessing': 'File Processing',
		'ui.details.fileProcessingProgress':
			'{{processed}} of {{total}} files processed{{failedSummary}}{{pendingSummary}}.',
		'ui.details.filesFailedSummary': ', {{total}} failed',
		'ui.details.filesPendingSummary': ', {{total}} pending',
		'ui.details.filteredFileCount': 'showing {{shown}} of {{total}} files',
		'ui.details.loadFailed': "Couldn't load this step's details.",
		'ui.details.narrative': 'Narrative',
		'ui.details.noData': '(no data)',
		'ui.details.noFilesToProcess': 'No files were provided for processing.',
		'ui.details.phaseProcessingWarning':
			'Some information could not be processed. Review the phase summary for next steps.',
		'ui.details.policyActive': 'Active',
		'ui.details.policyCondition': 'Condition:',
		'ui.details.policyDuration': 'Completed in {{seconds}}s',
		'ui.details.policyInactive': 'Inactive',
		'ui.details.policyPriority': 'Priority: {{priority}}',
		'ui.details.policyRule': 'Policy rule',
		'ui.details.processedFile': 'Processed file',
		'ui.details.processingNotes': 'Processing Notes:',
		'ui.details.projectMemory.filtersApplied': '{{total}} filters applied.',
		'ui.details.projectMemory.oneFilterApplied': '{{total}} filter applied.',
		'ui.details.projectMemory.oneResultReturned':
			'{{total}} result was returned. Business fields are not included in this trace receipt.',
		'ui.details.projectMemory.resultsReturned':
			'{{total}} results were returned. Business fields are not included in this trace receipt.',
		'ui.details.projectMemory.valuesShortened':
			'Some long values are shortened in this trace. The run used the full values.',
		'ui.details.ruleColumn': 'Rule',
		'ui.details.ruleResult': 'Result:',
		'ui.details.runSummary': 'Run Summary',
		'ui.details.showAllRows': 'Show all {{total}} rows',
		'ui.details.showFirstRows': 'Show first {{total}} rows',
		'ui.details.showLess': 'show less',
		'ui.details.showMore': 'show {{total}} more',
		'ui.details.sopRules': 'SOP Rules',
		'ui.details.temporarilyUnavailable': 'Details are temporarily unavailable.',
		'ui.details.unnamedRule': 'Unnamed rule',
		'ui.details.validationOutcome': 'Validation outcome',
		'ui.details.validationSummary': 'Validation summary',
		'ui.details.viewPhase': 'View {{phase}}',
		'ui.graphEnrichment.fieldsReadSummary':
			'{{total}} source fields were read.',
		'ui.graphEnrichment.oneFieldRead': '{{total}} source field was read.',
		'ui.header.dispatchWait': 'DISPATCH WAIT',
		'ui.header.fileWait': 'FILE WAIT',
		'ui.header.operations': 'OPERATIONS',
		'ui.header.stepsShown': 'STEPS SHOWN',
		'ui.header.billableTasks': 'BILLABLE TASKS',
		'ui.header.billing.calculating': 'Calculating',
		'ui.header.billing.estimated': 'estimated',
		'ui.header.billing.calculated': 'calculated',
		'ui.header.billing.delayed': 'Delayed',
		'ui.header.billing.unavailable': 'Unavailable',
		'ui.header.billing.notBillable': 'Not billable',
		'ui.instanceDataFieldCount': '{{total}} fields',
		'ui.instanceDataFieldsAvailable': '{{total}} fields available',
		'ui.instanceDataOneSchemaSummary':
			'{{fields}} fields across {{total}} schema',
		'ui.instanceDataPrimary': 'Primary',
		'ui.instanceDataRecordedFields': 'Recorded input fields',
		'ui.instanceDataRecordedSchemas': 'Input schemas',
		'ui.instanceDataSchema': 'Schema',
		'ui.instanceDataSchemaSummary':
			'{{fields}} fields across {{total}} schemas',
		'ui.instanceDataSchemasLimited':
			'Some schema details were shortened or omitted from this trace.',
		'ui.instanceDataSharedFieldsAvailable': '{{total}} shared fields available',
		'ui.instanceDataValuesOmitted':
			'Some input values were omitted from this trace because they exceed the trace size limit.',
		'ui.metric.dispatchWait': 'Dispatch wait',
		'ui.metric.fileWait': 'File processing wait',
		'ui.retry': 'Retry',
		'ui.status.approved': 'Approved',
		'ui.status.cancelled': 'Cancelled',
		'ui.status.completed': 'Completed',
		'ui.status.error': 'Error',
		'ui.status.failed': 'Failed',
		'ui.status.flagged': 'Flagged',
		'ui.status.insufficientData': 'Insufficient Data',
		'ui.status.noActionNeeded': 'No Action Needed',
		'ui.status.partiallyCompleted': 'Partially Completed',
		'ui.status.passed': 'Passed',
		'ui.status.pending': 'Pending',
		'ui.status.requiresReview': 'Requires Review',
		'ui.status.running': 'Running',
		'ui.status.skipped': 'Skipped',
		'ui.status.success': 'Success',
		'ui.status.violated': 'Violated',
		'ui.status.violationsFound': 'Violations Found',
		'ui.traceLoadError': "We couldn't load this trace. Please try again.",
		'ui.viewSwitchError': "We couldn't switch trace views. Please try again.",
		'ui.details.documentScope': 'Document scope: {{scope}}',
		'ui.details.extractionRevision': 'Extraction revision: {{revision}}',
		'ui.details.fileFailureCode': 'Failure code: {{code}}',
		'ui.details.fileOutcomes': 'File outcomes',
		'ui.details.filesProcessed': 'Processed',
		'ui.details.filesTotal': 'Total files',
		'ui.details.pageLevelScope': 'Page level',
		'ui.details.unnamedFile': 'Unnamed file',
		'ui.details.wholeFileScope': 'Whole file',
		'ui.fileStatus.archiveExtracted': 'Archive extracted',
		'ui.fileStatus.archiveExtracting': 'Extracting archive',
		'ui.fileStatus.archiveFailed': 'Archive extraction failed',
		'ui.fileStatus.completed': 'Completed',
		'ui.fileStatus.failed': 'Failed',
		'ui.fileStatus.pending': 'Pending',
		'ui.fileStatus.pendingUpload': 'Pending upload',
		'ui.fileStatus.processing': 'Processing',
		'ui.fileStatus.retry': 'Retry',
		'ui.fileStatus.unknown': 'Unknown status',
		'ui.details.allRequestedEnrichmentFailed':
			'No requested enrichment rule produced a result.',
		'ui.details.exceptionType': 'Exception type',
		'ui.details.noValidationVerdict': 'No validation rules produced a verdict.',
		'ui.details.requiredEnrichmentFailed':
			'Required enrichment did not complete.',
		'ui.details.requiredWorkflowSearchFailed':
			'Required workflow search did not complete.',
		'ui.details.ruleCodeLine': 'Rule code line',
		'ui.details.ruleDiagnostics': 'Diagnostics',
		'ui.details.ruleErrorReference': 'Error reference',
		'ui.details.ruleReason': 'Reason',
		'ui.details.sourcesCompared': 'Sources compared',
		'ui.details.validationAllSkipped': 'All validation rules were skipped.',
		'ui.details.validationConfigurationError':
			'Validation configuration was incomplete.',
		'ui.details.validationDegradation': 'Reduced validation coverage',
		'ui.details.validationOutcomeRuleCount': 'Affected rules: {{total}}',
		'ui.details.validationSelectedRuleCount': 'Selected rules: {{total}}',
		'ui.graphEnrichment.noMatchingExtraction':
			'No extracted document data matched the configured graph node fields. Check the extraction data and graph enrichment rule configuration.',
		'ui.graphEnrichment.noNodeFields':
			'No graph node fields are configured. Check the graph enrichment rule configuration.',
		'ui.graphEnrichment.ruleFailed':
			'A graph enrichment rule could not complete.',
		'ui.graphEnrichment.totalRules': 'Total graph rules',
		'ui.collation.collatedData': 'Collated Data',
		'ui.collation.noOutputData': 'No output data.',
		'ui.collation.primary': 'primary',
		'ui.collation.secondary': 'secondary',
		'ui.collation.totalRules': 'Total rules',
		'ui.collation.unnamedRule': 'Unnamed collation rule',
		'ui.details.projectMemory.entityPayloadsOmitted':
			'Business fields are not included in this trace receipt.',
		'ui.details.projectMemory.entityTypes': 'Entity types',
		'ui.details.projectMemory.fetchedEntities': 'Fetched entities',
		'ui.details.projectMemory.incompleteResults':
			'The read reported incomplete results.',
		'ui.details.projectMemory.indexedCandidates': 'Indexed candidates',
		'ui.details.projectMemory.noRecordedFilters':
			'No filter entries were recorded.',
		'ui.details.projectMemory.noReturnedEntities': 'No entities were returned.',
		'ui.details.projectMemory.oversizeSkipped': 'Oversize results skipped',
		'ui.details.projectMemory.readCompleted': 'Read completed',
		'ui.details.projectMemory.readFailed': 'Read failed',
		'ui.details.projectMemory.readFailureExplanation':
			'Project Memory could not be read.',
		'ui.details.projectMemory.readNotUsed':
			'Candidate and returned counts describe this read, not downstream use.',
		'ui.details.projectMemory.readTitle': 'Project Memory read',
		'ui.details.projectMemory.recordedCompletenessTimestamp':
			'Recorded completeness timestamp: {{timestamp}}',
		'ui.details.projectMemory.recordedEntityDetails': 'Recorded entity details',
		'ui.details.projectMemory.recordedFilters': 'Recorded filter entries',
		'ui.details.projectMemory.returnedEntities': 'Returned entities',
		'ui.details.projectMemory.returnedSliceByType':
			'Entities by type (returned slice)',
		'ui.details.projectMemory.sourceWorkflowIds': 'Source workflow IDs',
		'ui.details.projectMemory.traceDetailsLimited':
			'Some trace details were shortened or omitted.',
		'ui.details.projectMemory.unrecordedResultCounts':
			'Returned-result counts were not recorded.',
		'ui.details.narrativeReview.emptyOutput':
			'Narrative generation returned empty output.',
		'ui.details.narrativeReview.generationFailed':
			'Narrative generation failed',
		'ui.details.narrativeReview.invalidSchema':
			'The configured narrative schema was invalid.',
		'ui.instanceDataCombinedPreviewOmitted':
			'The combined input preview was omitted from this trace because it exceeds the trace size limit.',
		'ui.instanceInputs.combined': 'Combined inputs used by the rules',
		'ui.instanceInputs.fieldNamesLimited':
			'Not all field names are included in this trace receipt.',
		'ui.instanceInputs.schemaCount': 'Recorded schemas: {{total}}',
		'ui.instanceInputs.schemas': 'Inputs by schema',
		'ui.instanceInputs.sharedCount': 'Recorded shared sources: {{total}}',
		'ui.instanceInputs.sharedSources': 'Shared input sources',
		'ui.instanceInputs.sourceUnnamed': 'Source name was not recorded',
		'ui.instanceInputs.valuesOmitted':
			'Per-schema values were omitted from this bounded trace receipt; runtime inputs were not changed.',
		'ui.memoryScope.alternatives': 'Selection alternatives (OR)',
		'ui.memoryScope.conditions': 'Conditions within this alternative (AND)',
		'ui.memoryScope.effective': 'Effective limit: {{total}}',
		'ui.memoryScope.explanation':
			'Recorded global conditions apply to each alternative. Only configured selection fields are shown; filter values and server constraints are not included.',
		'ui.memoryScope.global': 'Global conditions (AND)',
		'ui.memoryScope.requested': 'Requested limit: {{total}}',
		'ui.memoryScope.title': 'Recorded query scope',
		'ui.narrativeContext.schemaConfigured': 'Schema guidance configured: Yes',
		'ui.narrativeContext.schemaNotConfigured': 'Schema guidance configured: No',
		'ui.ruleContext.bound': 'Bound',
		'ui.ruleContext.declared': 'Declared inputs',
		'ui.ruleContext.declaredCount': 'Declared input schemas: {{total}}',
		'ui.ruleContext.entries': 'Resolved entries: {{total}}',
		'ui.ruleContext.intent': 'Recorded rule intent',
		'ui.ruleContext.missing': 'Missing matching input entries',
		'ui.ruleContext.noneDeclared': 'No inputs were declared.',
		'ui.ruleContext.notActualUse':
			'Resolved inputs do not establish which fields the rule actually read.',
		'ui.ruleContext.outputSchema': 'Output schema',
		'ui.ruleContext.rule': 'Rule',
		'ui.ruleContext.title': 'Resolved rule inputs',
		'ui.ruleContext.type': 'Rule type',
		'ui.ruleContext.unnamed': 'Schema name was not recorded',
		'ui.runConfiguration.version': 'Applied workflow version',
		'ui.sourceReceipt.authenticationFailed': 'Source authentication failed',
		'ui.sourceReceipt.backup': 'Journal backup selected',
		'ui.sourceReceipt.deferred': 'Deferred to enrichment provider',
		'ui.sourceReceipt.emptyQuery': 'Source query is empty',
		'ui.sourceReceipt.excluded': 'Excluded records',
		'ui.sourceReceipt.failureCode': 'Failure code',
		'ui.sourceReceipt.failureSummary': 'Failure explanation',
		'ui.sourceReceipt.fetchTitle': 'Source fetch',
		'ui.sourceReceipt.fetched': 'Fetched records',
		'ui.sourceReceipt.fresh': 'Fresh response selected',
		'ui.sourceReceipt.httpStatus': 'HTTP status',
		'ui.sourceReceipt.invalidResponse': 'Source response could not be read',
		'ui.sourceReceipt.method': 'Method',
		'ui.sourceReceipt.missingEndpoint': 'Source endpoint is not configured',
		'ui.sourceReceipt.outcome': 'Fetch outcome',
		'ui.sourceReceipt.preparationFailed':
			'Source request could not be prepared',
		'ui.sourceReceipt.received': 'Response received',
		'ui.sourceReceipt.requestFailed': 'Source request failed',
		'ui.sourceReceipt.routed': 'Routed records',
		'ui.sourceReceipt.routingTitle': 'Source selection',
		'ui.sourceReceipt.schemaBound': 'Schema-bound records',
		'ui.sourceReceipt.selectionNotUse':
			'Selection and routing do not establish which fields downstream steps used.',
		'ui.sourceReceipt.source': 'Source',
		'ui.sourceReceipt.timeout': 'Source request timed out',
		'ui.sourceReceipt.type': 'Source type',
		'ui.sourceReceipt.unavailable': 'No response selected',
		'ui.sourceReceipt.unboundTypes': 'Entity types without a schema binding',
		'ui.agentOutputs.actionCount': 'Recorded actions: {{total}}',
		'ui.agentOutputs.affectedFields': 'Recorded fields: {{total}}',
		'ui.agentOutputs.extract': 'Extract and store workflow data',
		'ui.agentOutputs.failed': 'Failed',
		'ui.agentOutputs.noChange': 'No change',
		'ui.agentOutputs.primary': 'Recorded primary outputs',
		'ui.agentOutputs.saved': 'Saved',
		'ui.agentOutputs.storage': 'Recorded storage outcomes',
		'ui.agentOutputs.store': 'Store workflow data',
		'ui.agentOutputs.timeout': 'Timed out',
		'ui.agentOutputs.unknown': 'Outcome not recorded',
		'ui.details.policyEvaluations': 'Policy Evaluations',
		'ui.inputContext.agent': 'Agent remediation',
		'ui.inputContext.collation': 'Collation results',
		'ui.inputContext.documents': 'Document records',
		'ui.inputContext.entityData': 'Entity data',
		'ui.inputContext.entitySummary': 'Entity summary',
		'ui.inputContext.entries': 'Recorded entries: {{total}}',
		'ui.inputContext.extractions': 'File extractions',
		'ui.inputContext.graphSupplied':
			'Raw file extractions were not supplied because graph context was supplied.',
		'ui.inputContext.knowledge': 'Knowledge base',
		'ui.inputContext.narrativeStage': 'Prepared narrative context',
		'ui.inputContext.notCitations':
			'These records describe context prepared for this attempt, not which fields the model read or which evidence it cited.',
		'ui.inputContext.policies': 'Policies',
		'ui.inputContext.reconciliation': 'Reconciliation results',
		'ui.inputContext.schemas': 'Instance schemas',
		'ui.inputContext.shared': 'Shared workflows',
		'ui.inputContext.sources': 'Data sources',
		'ui.inputContext.textStage': 'Prepared text context',
		'ui.inputContext.title': 'Recorded input context',
		'ui.inputContext.validation': 'Validation results',
		'ui.inputContext.visualFailed': 'Visual preparation failures',
		'ui.inputContext.visualFiles': 'Prepared visual files',
		'ui.inputContext.visualStage': 'Visual preparation',
		'ui.narrativeContext.bindingCount': 'Bound fields: {{total}}',
		'ui.narrativeContext.bindings': 'Recorded field bindings',
		'ui.narrativeContext.deterministic':
			'Copied deterministically from a rule result',
		'ui.policyCompletion.completed': 'Completed usable results',
		'ui.policyCompletion.failure':
			'Policy evaluation did not finish; incomplete policies still require review.',
		'ui.policyCompletion.incomplete': 'Incomplete policies',
		'ui.policyCompletion.noResult':
			'Policies without a completed usable result',
		'ui.policyCompletion.position': 'Request position: {{position}}',
		'ui.policyCompletion.targeted':
			'Completion facts describe this targeted request, not the entire policy batch.',
		'ui.policyCompletion.total': 'Policies in this request',
		'ui.policyCompletion.unnamed': 'Policy name was not recorded',
		'ui.policyEvidence.cited': 'Evidence cited:',
		'ui.policyRequirements.missing': 'Missing Information:',
		'ui.policyRequirements.requested': 'Requested Attachments:',
		'ui.reuse.baseline':
			'This step did not execute in this run. Recorded results were reused from the baseline run.',
		'ui.reuse.journal':
			'This step did not execute in this run. Displayed inputs were loaded from the current instance journal.',
		'ui.reuse.loadedFrom':
			'This step did not execute in this run. Its data was loaded from {{source}}.',
		'ui.reuse.recordsTitle': 'Data carried into this run',
		'ui.reuse.sourcesTitle': 'Sources reused',
		'ui.sourceReceipt.reusedTitle': 'Recorded source data',
		'ui.details.internalTraceMetadata': 'Internal trace metadata',
		'ui.details.showInitialPlan': 'Show initial plan',
		'ui.details.technicalDetails': 'Technical details',
		'ui.details.toolCalls': 'Tool Calls:',
		'ui.metric.knownCost':
			'at least {{cost}} ({{costed}} of {{total}} calls priced)',
		'ui.tree.durationUnavailable': 'Duration not recorded',
		'ui.tree.recordedDuration': 'Recorded duration: {{duration}}',
	},
	workflow_builder: {
		'dashboard.title': 'Dashboard',
		'zeroState.panelTitle': 'Get started with creating your first workflow',
		'zeroState.headline': 'No workflows yet',
		'zeroState.body':
			'Build your first automated workflow or get started quickly with a ready-made example.',
		'zeroState.newWorkflow': 'New Workflow',
		'zeroState.scratch.eyebrow': 'Start from scratch',
		'zeroState.scratch.title': 'Explore on your own',
		'zeroState.scratch.description':
			'Jump straight into the workflow builder and create your own automation from the ground up.',
		'zeroState.scratch.cta': 'Open builder',
		'zeroState.example.eyebrow': 'Recommended example',
		'zeroState.example.title':
			'Start with Expense Reimbursement Policy Driven workflow',
		'zeroState.example.description':
			'Seed your account with a pre-configured example so you can see how a real workflow is set up before building your own.',
		'zeroState.example.cta': 'Use this example',
		'zeroState.example.loadingCta': 'Installing example',
		'zeroState.checkingPermissions': 'Checking permissions…',
		'zeroState.noAccess':
			"You don't have permission to create workflows. Ask your admin for access.",
		'workflows.activationSummary.manual':
			'Applied when explicitly selected on an instance.',
		'workflows.activationSummary.auto':
			'Applies automatically when {{conditions}}.',
		'workflows.activationSummary.joinerAnd': ' and ',
		'workflows.activationSummary.joinerOr': ' or ',
		'workflows.activationSummary.operator.is': 'is',
		'workflows.activationSummary.operator.isNot': 'is not',
		'workflows.activationSummary.operator.contains': 'contains',
		'workflows.activationSummary.operator.isOneOf': 'is one of',
		'workflows.activationSummary.operator.isGreaterThan': 'is greater than',
		'workflows.activationSummary.operator.isAtLeast': 'is at least',
		'workflows.activationSummary.operator.isLessThan': 'is less than',
		'workflows.activationSummary.operator.isAtMost': 'is at most',
		'workflows.profilesIndex.title': 'Profiles',
		'workflows.profilesIndex.allProfiles': 'All Profiles',
		'workflows.profilesIndex.searchPlaceholder': 'Search',
		'workflows.profilesIndex.newProfile': 'New Profile',
		'workflows.profilesIndex.columnProfile': 'Profile',
		'workflows.profilesIndex.columnActivation': 'Activation',
		'workflows.profilesIndex.columnFileClasses': 'File classes',
		'workflows.profilesIndex.columnPolicies': 'Policies',
		'workflows.profilesIndex.baselineTitle': 'Baseline',
		'workflows.profilesIndex.baselineSubtitle':
			'Always applies — even when no profile matches',
		'workflows.profilesIndex.baselineAlwaysOn': 'Always on',
		'workflows.profilesIndex.baselineExpandedNote':
			'Applies to every instance in this workflow. Profiles layer on top — they can add their own classes and policies, or suppress these.',
		'workflows.profilesIndex.baselineClassesNote':
			'Every instance is classified with these — profiles can add or suppress classes on top.',
		'workflows.profilesIndex.baselinePoliciesNote':
			'Every instance runs these — profiles can add or suppress policies on top.',
		'workflows.profilesIndex.emptyTitle': 'No profiles yet',
		'workflows.profilesIndex.emptySearchTitle': 'No profiles match your search',
		'workflows.profilesIndex.emptySearchHint': 'Try a different search term',
		'workflows.profilesIndex.emptyCreateHint':
			'Baseline covers every instance by default. Create a profile when a lender, customer, or state needs its own classes and policies layered on top.',
		'workflows.profilesIndex.createDialogTitle': 'Create Profile',
		'workflows.profilesIndex.createDialogDescription':
			'Create a profile for a customer, state, lender, or claim type, then choose when it should be used.',
		'workflows.profilesIndex.chipAutoMatch': 'Auto-match',
		'workflows.profilesIndex.chipManual': 'Manual',
		'workflows.profilesIndex.policyFallback': 'Policy',
		'workflows.profilesIndex.classesTooltipUnloaded':
			"Expand to load this profile's file classes; plus {{baseline}} inherited from Baseline",
		'workflows.profilesIndex.assignedTooltip':
			'{{assigned}} assigned to this profile, plus {{baseline}} inherited from Baseline',
		'workflows.profilesIndex.plusBaseline': '+{{baseline}} Baseline',
		'workflows.profilesIndex.editProfile': 'Edit Profile',
		'workflows.profilesIndex.delete': 'Delete',
		'workflows.profilesIndex.inheritsClassesNote':
			'Inherits {{baseline}} from Baseline · profile classes override on conflict',
		'workflows.profilesIndex.inheritsPoliciesNote':
			'Inherits {{baseline}} from Baseline',
		'workflows.profilesIndex.editInStep2': 'Edit in Step 2 →',
		'workflows.profilesIndex.editInStep4': 'Edit in Step 4 →',
		'workflows.profilesIndex.assignedLabel': 'assigned',
		'workflows.profilesIndex.moreCount': '+{{more}} more',
		'workflows.profilesIndex.editDialogTitle': 'Edit Profile',
		'workflows.profilesIndex.editDialogDescription':
			"Update this profile's name, description, and matching rules.",
		'workflows.profilesIndex.deleteDialogTitle': 'Delete {{name}}?',
		'workflows.profilesIndex.profileFallback': 'profile',
		'workflows.profilesIndex.deleteDialogDescription':
			'This removes the profile and both of its assignment sets ({{classCount}} file classes, {{policyCount}} policies). The Library classes and policies themselves are not deleted — future instances fall back to Baseline.',
		'workflows.profilesIndex.deleting': 'Deleting...',
		'workflows.profilesIndex.deleteProfile': 'Delete Profile',
		'workflows.addPolicyFromLibraryDialog.title': 'Add from Library',
		'workflows.addPolicyFromLibraryDialog.descriptionPrefix':
			'Pick policies from the Library to assign to',
		'workflows.addPolicyFromLibraryDialog.descriptionProfile': ' profile',
		'workflows.addPolicyFromLibraryDialog.descriptionSuffix':
			". Assigning doesn't change the policy itself — editing a policy later updates it everywhere it's used.",
		'workflows.addPolicyFromLibraryDialog.searchPlaceholder': 'Search policies',
		'workflows.addPolicyFromLibraryDialog.searchAriaLabel':
			'Search Library policies',
		'workflows.addPolicyFromLibraryDialog.noPoliciesToAdd':
			'No policies to add',
		'workflows.addPolicyFromLibraryDialog.noPoliciesMatchSearch':
			'No policies match your search',
		'workflows.addPolicyFromLibraryDialog.noPoliciesToAddHint':
			'Every Library policy is already assigned here. Use "Add Policy" in the Library to create more.',
		'workflows.addPolicyFromLibraryDialog.selectedCount':
			'{{selected}} selected',
		'workflows.addPolicyFromLibraryDialog.selectPoliciesHint':
			'Select policies to assign',
		'workflows.addPolicyFromLibraryDialog.cancel': 'Cancel',
		'workflows.addPolicyFromLibraryDialog.adding': 'Adding…',
		'workflows.addPolicyFromLibraryDialog.addCountToTarget':
			'Add {{selected}} to {{target}}',
		'workflows.addPolicyFromLibraryDialog.addToTarget': 'Add to {{target}}',
		'workflows.configSideNav.profiles': 'Profiles',
		'workflows.configSideNav.toolsRegistry': 'Tools Registry',
		'workflows.configSideNav.exportWorkflow': 'Export workflow',
		'workflows.configSideNav.versionHistory': 'Version History',
		'workflows.configSideNav.jobs': 'Jobs',
		'workflows.configSideNav.deploy': 'Deploy',
		'workflows.configSideNav.deploying': 'Deploying...',
		'workflows.configSideNav.deployTooltip': 'Deploy this branch for testing',
		'workflows.configSideNav.collapseMenu': 'Collapse Menu',
		'workflows.configSideNav.expandMenu': 'Expand Menu',
		'workflows.addFromLibraryLabel': 'Add from Library',
		'workflows.modelTiers.extractionLabel': 'Extraction model',
		'workflows.modelTiers.extractionScope':
			'Applies to all classification and extraction tasks in this step.',
		'workflows.modelTiers.collationLabel': 'Collation model',
		'workflows.modelTiers.collationScope':
			'Applies to every rule in this step.',
		'workflows.modelTiers.policyLabel': 'Policy model',
		'workflows.modelTiers.policyScope': 'Applies to every policy in this step.',
		'workflows.modelTiers.agentLabel': 'Agent model',
		'workflows.modelTiers.agentScope':
			'Applies to every model call in this step.',
		'workflows.modelTiers.defaultLabel': 'Default',
		'workflows.modelTiers.bulletproofLabel': 'Bulletproof',
		'workflows.modelTiers.defaultDescription':
			'Balanced speed and cost for most workflows.',
		'workflows.modelTiers.bulletproofDescription':
			'Strongest approved stack. Use it when a wrong answer costs more than the run.',
		'workflows.modelTiers.currentConfiguration': 'Current configuration',
		'workflows.modelTiers.modelLabel': 'Model',
		'workflows.modelTiers.loadError': 'Could not load model tiers.',
		'workflows.modelTiers.updateError':
			'Could not save the model selection. Please try again.',
		'workflows.modelTiers.loading': 'Loading model tiers…',
		'workflows.modelTiers.unavailable': 'Model tiers are unavailable.',
		'workflows.modelTiers.triggerAriaLabel': '{{model}} {{value}}',
		'workflows.modelTiers.menuLabel': '{{label}} tiers',
		'workflows.modelTiers.recommended': 'Recommended',
		'workflows.modelTiers.costLabel': 'Cost',
		'workflows.modelTiers.speedLabel': 'Speed',
		'workflows.modelTiers.signalAriaLabel':
			'{{label}}: {{active}} of {{total}}',
		'workflows.modelTiers.saving': 'Saving model tier',
		'workflows.modelTiers.repairTitle': 'Repair extraction model',
		'workflows.modelTiers.repairDescription':
			'The saved model tier configuration is invalid. Select an extraction tier to repair the extraction setting. If the save fails, contact support.',
		'workflows.modelTiers.repairAction': 'Repair extraction tier',
		'workflows.modelTiers.repairPlaceholder': 'Select a tier',
		'workflows.modelTiers.repairCancel': 'Cancel',
		'workflows.modelTiers.repairSaving': 'Saving…',
		'workflows.modelTiers.repairSave': 'Save extraction tier',
		'workflows.modelTiers.repairAgentTitle': 'Repair agent model',
		'workflows.modelTiers.repairAgentDescription':
			'The saved agent model tier is invalid. Select an agent tier to repair the agent setting. If the save fails, contact support.',
		'workflows.modelTiers.repairAgentAction': 'Repair agent tier',
		'workflows.modelTiers.repairAgentSave': 'Save agent tier',
		'workflows.modelTiers.invalidTier':
			'The saved tier for this step is invalid.',
		'workflows.modelTiers.repairError':
			'Could not repair the model tier configuration. Please try again.',
		'workflows.fileProcessing.page.manageProfiles': 'Manage Profiles',
		'workflows.fileProcessing.classificationRow.deleteClassification':
			'Delete classification',
		'workflows.fileProcessing.classificationRow.deleteClassificationNamed':
			'Delete classification {{name}}',
		'workflows.fileProcessing.classificationRow.removeFromProfile':
			'Remove from profile',
		'workflows.fileProcessing.classificationRow.removeFromProfileNamed':
			'Remove {{name}} from profile',
		'workflows.policyLibraryEmptyState.addPolicy': 'Add Policy',
		'workflows.policyProfilesSidebarManageProfiles': 'Manage profiles →',
	},
	platform_ui: {
		'nav.workflows': 'Workflows',
		'nav.settings': 'Settings',
		'nav.usage': 'Usage',
		'nav.help': 'Help',
		'nav.installCli': 'Install CLI',
		'settings.general': 'General',
		'settings.personalization': 'Personalization',
		'settings.userNotifications': 'User Notifications',
		'settings.privacy': 'Privacy',
		'settings.team': 'Team',
		'settings.translations': 'Translations',
	},
};

export const NAMESPACES = Object.keys(EN_FALLBACK);
